import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { Drawer } from '../../components/ui/Drawer';
import adminService, {
  SystemLog,
  SystemLogsStatsResponse,
  SystemLogCodeSite,
  SystemLogCodeSiteCount,
} from '../../services/admin.service';
import { LOG_AUTO_REFRESH_MS, DetailField, NONE } from './logPageShared';

/**
 * Logging → System Logs.
 *
 * The whole table is pulled in one request and handed to TanTable, which
 * virtualizes the rows and does sorting / per-column filtering / search /
 * Excel export on the client. That replaces the old server-paginated
 * 50-row view plus its hand-rolled level/category/search controls — those
 * are now column filters.
 *
 * Row cells are deliberately single-line: TanTable's virtualizer assumes a
 * fixed row height, and multi-line cells make virtualized rows overlap (see
 * WebhookActivity, which had to disable virtualization for that reason).
 * Everything that needs room — stack trace, exception, process info — lives
 * in the slide-out drawer, along with the two delete actions.
 */

// Matches SYSTEM_LOGS_MAX_LIMIT on the server, and is higher than the other log
// pages' LOG_FETCH_ALL_LIMIT: system_logs is cleared routinely and stays in the
// tens of thousands, so this really does pull everything.
const FETCH_ALL_LIMIT = 200000;

/** "app.middleware.audit_middleware_pure_asgi → __call__() line 238" */
const formatCodeSite = (site: SystemLogCodeSite): string =>
  [
    site.logger_name,
    site.function ? `→ ${site.function}()` : null,
    site.line_number ? `line ${site.line_number}` : null,
  ]
    .filter(Boolean)
    .join(' ');

const getLevelBadgeColor = (level: string) => {
  switch (level) {
    case 'ERROR':
    case 'CRITICAL':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
    case 'WARNING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800';
    case 'INFO':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
    default:
      return 'bg-gray-100 text-gray-800 border-rule dark:bg-gray-700 dark:text-gray-200';
  }
};

const getCategoryBadgeColor = (category: string) => {
  switch (category) {
    case 'SYNC':
      return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800';
    case 'API':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
    case 'AUTH':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
    case 'DATABASE':
      return 'bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800';
    case 'SYSTEM':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800';
    default:
      return 'bg-gray-100 text-gray-800 border-rule dark:bg-gray-700 dark:text-gray-200';
  }
};

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  // Logs are stored UTC; operators read them in terminal time.
  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const SystemLogs: React.FC = () => {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [stats, setStats] = useState<SystemLogsStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selected, setSelected] = useState<SystemLog | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [codeSiteCount, setCodeSiteCount] = useState<SystemLogCodeSiteCount | null>(null);
  const [codeSiteLoading, setCodeSiteLoading] = useState(false);
  const logsAbortRef = useRef<AbortController | null>(null);
  const statsAbortRef = useRef<AbortController | null>(null);
  const codeSiteAbortRef = useRef<AbortController | null>(null);

  const fetchLogs = useCallback(async () => {
    logsAbortRef.current?.abort();
    const controller = new AbortController();
    logsAbortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await adminService.getSystemLogs(
        { limit: FETCH_ALL_LIMIT },
        controller.signal
      );
      setLogs(response.logs);
      setTotalLogs(response.total);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || 'Failed to fetch logs');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    statsAbortRef.current?.abort();
    const controller = new AbortController();
    statsAbortRef.current = controller;

    try {
      setStats(await adminService.getSystemLogsStats(controller.signal));
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch log stats:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchLogs();
    await fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all system logs? This action cannot be undone.')) {
      return;
    }

    setClearing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await adminService.clearSystemLogs();
      setSuccessMessage(`Successfully cleared ${result.deleted_count.toLocaleString()} log entries`);
      setSelected(null);
      await refresh();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear logs');
    } finally {
      setClearing(false);
    }
  };

  const handleDeleteLog = async (logId: number) => {
    if (!window.confirm('Delete this log entry? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await adminService.deleteSystemLog(logId);
      setSuccessMessage('Deleted 1 log entry');
      setSelected(null);
      await refresh();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete log entry');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSimilar = async (log: SystemLog) => {
    // codeSiteCount is fetched when the drawer opens; fall back to the row's own
    // fields if that request hasn't landed (or failed) so the action still works.
    const site: SystemLogCodeSite = codeSiteCount ?? {
      logger_name: log.logger_name,
      function: log.function ?? null,
      line_number: log.line_number ?? null,
    };
    const countLabel = codeSiteCount ? codeSiteCount.count.toLocaleString() : 'all';

    if (
      !window.confirm(
        `Delete ${countLabel} log entries from ${formatCodeSite(site)}?\n\n` +
          'Every entry from this line is removed, whatever its message. This action cannot be undone.'
      )
    ) {
      return;
    }

    setDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await adminService.deleteSimilarSystemLogs(log.id);
      setSuccessMessage(
        `Deleted ${result.deleted_count.toLocaleString()} log entries from ${formatCodeSite(result)}`
      );
      setSelected(null);
      await refresh();
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete similar log entries');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    refresh();

    return () => {
      logsAbortRef.current?.abort();
      statsAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVisibilityAwareInterval(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, autoRefresh ? LOG_AUTO_REFRESH_MS : null);

  // Count how many entries share the open row's code site, so the bulk delete
  // button can name the number before anyone commits to it.
  useEffect(() => {
    codeSiteAbortRef.current?.abort();
    setCodeSiteCount(null);

    if (!selected) return;

    const controller = new AbortController();
    codeSiteAbortRef.current = controller;
    setCodeSiteLoading(true);

    adminService
      .getSimilarSystemLogCount(selected.id, controller.signal)
      .then((result) => setCodeSiteCount(result))
      .catch((err: any) => {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        console.error('Failed to count similar logs:', err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setCodeSiteLoading(false);
      });

    return () => controller.abort();
  }, [selected]);

  const columns = useMemo<ColumnDef<SystemLog>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: (info) => (
          <span className="whitespace-nowrap">{formatTimestamp(info.getValue() as string)}</span>
        ),
        size: 175,
      },
      {
        accessorKey: 'level',
        header: 'Level',
        cell: (info) => {
          const level = info.getValue() as string;
          return (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium border ${getLevelBadgeColor(level)}`}
            >
              {level}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: (info) => {
          const category = info.getValue() as string | undefined;
          if (!category) return <span className="text-gray-400">—</span>;
          return (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium border ${getCategoryBadgeColor(category)}`}
            >
              {category}
            </span>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'logger_name',
        header: 'Logger',
        cell: (info) => {
          const v = info.getValue() as string;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 280,
      },
      {
        // Single sortable/filterable column for the code site, which is also what
        // "Delete All Like This" matches on.
        id: 'code_site',
        accessorFn: (row) =>
          row.function ? `${row.function}() : ${row.line_number ?? ''}` : '',
        header: 'Function : Line',
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 190,
      },
      {
        accessorKey: 'message',
        header: 'Message',
        cell: (info) => {
          const row = info.row.original;
          const v = info.getValue() as string;
          // Single line only — the virtualizer assumes fixed row heights. Full
          // text and any exception detail are in the drawer.
          const full = row.exception_message
            ? `${v}\n\n${row.exception_type}: ${row.exception_message}`
            : v;
          return (
            <span className="block truncate" title={full}>
              {v}
              {row.exception_message && (
                <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                  {row.exception_type}
                </span>
              )}
            </span>
          );
        },
        size: 620,
      },
    ],
    []
  );

  const truncated = totalLogs > logs.length;

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-heading">System Logs</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Application logs (WARNING and above). Click any row for the stack trace and
            cleanup actions.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={`Auto-refresh every ${LOG_AUTO_REFRESH_MS / 1000}s`}
            className={`px-3 py-2 rounded-md border text-sm ${
              autoRefresh
                ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300'
                : 'bg-gray-50 border-gray-300 text-label dark:bg-gray-700 dark:border-gray-600'
            }`}
          >
            Auto: {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={handleClearLogs}
            disabled={clearing || loading}
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <TrashIcon className="w-4 h-4" />
            Clear Logs
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Logs</div>
            <div className="text-2xl font-bold text-heading">
              {stats.total_logs.toLocaleString()}
            </div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Errors (24h)</div>
            <div className="text-2xl font-bold text-red-600">{stats.recent_errors_24h}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">By Level</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {Object.entries(stats.by_level).map(([level, count]) => (
                <span
                  key={level}
                  className={`px-2 py-1 rounded text-xs font-medium border ${getLevelBadgeColor(level)}`}
                >
                  {level}: {count}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">By Category</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {Object.entries(stats.by_category).map(([category, count]) => (
                <span
                  key={category}
                  className={`px-2 py-1 rounded text-xs font-medium border ${getCategoryBadgeColor(category)}`}
                >
                  {category}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-300">
            <InformationCircleIcon className="w-5 h-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
            <XCircleIcon className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {truncated && (
        <div className="mb-2 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          Showing the {logs.length.toLocaleString()} most recent of{' '}
          {totalLogs.toLocaleString()} entries.
        </div>
      )}

      <TanTable
        data={logs}
        columns={columns}
        loading={loading}
        settingsKey="system-logs-table"
        fieldName="system-logs-table"
        cardTitle="System Logs"
        searchPlaceholder="Search logs…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        columnFilterTypes={{
          timestamp: 'text',
          logger_name: 'text',
          code_site: 'text',
          message: 'text',
        }}
        defaultSorting={[{ id: 'timestamp', desc: true }]}
        onRowClick={(row: SystemLog) => setSelected(row)}
      />

      <Drawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="xl"
        width="56rem"
        title={
          selected ? (
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded border ${getLevelBadgeColor(selected.level)}`}
              >
                {selected.level}
              </span>
              <span>Log Entry</span>
              <span className="text-xs text-muted">
                {formatTimestamp(selected.timestamp)}
              </span>
            </div>
          ) : (
            'Log Entry'
          )
        }
        footer={
          selected ? (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleDeleteLog(selected.id)}
                  disabled={deleting}
                  className="flex items-center gap-2 px-3 py-2 bg-panel border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-md text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete This Entry
                </button>
                <button
                  onClick={() => handleDeleteSimilar(selected)}
                  disabled={deleting || codeSiteLoading}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="w-4 h-4" />
                  {codeSiteLoading
                    ? 'Counting…'
                    : codeSiteCount
                    ? `Delete All ${codeSiteCount.count.toLocaleString()} Like This`
                    : 'Delete All Like This'}
                </button>
              </div>
              <p className="text-xs text-muted mt-2">
                "Like this" means the same logger, function and line number — the message
                is ignored, so entries with varying details are still matched.
              </p>
            </div>
          ) : undefined
        }
      >
        {selected && (
          <div className="space-y-5 text-sm">
            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Message
              </div>
              <div className="text-sm text-heading whitespace-pre-wrap break-words">
                {selected.message}
              </div>
              {selected.exception_message && (
                <div className="mt-2 text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                  {selected.exception_type}: {selected.exception_message}
                </div>
              )}
            </section>

            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Source
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                <DetailField label="Logger">{selected.logger_name}</DetailField>
                <DetailField label="Category">{selected.category || NONE}</DetailField>
                <DetailField label="Module">
                  {selected.module ? (
                    <>
                      {selected.module}
                      {selected.function && ` → ${selected.function}()`}
                      {selected.line_number != null && ` [Line ${selected.line_number}]`}
                    </>
                  ) : (
                    NONE
                  )}
                </DetailField>
                <DetailField label="Entry ID">{selected.id}</DetailField>
                <DetailField label="Timestamp">
                  {formatTimestamp(selected.timestamp)}
                </DetailField>
                <DetailField label="Process">
                  {selected.process_id || selected.thread_id ? (
                    <>
                      PID {selected.process_id ?? '—'} | Thread {selected.thread_id ?? '—'}
                    </>
                  ) : (
                    NONE
                  )}
                </DetailField>
              </dl>
            </section>

            {selected.stack_trace && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Stack Trace
                </div>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto whitespace-pre">
                  {selected.stack_trace}
                </pre>
              </section>
            )}
          </div>
        )}
      </Drawer>

      {logs.length === 0 && !loading && (
        <div className="text-center py-12">
          <InformationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No logs found</p>
        </div>
      )}
    </div>
  );
};

export default SystemLogs;
